import { useState } from "react";
import { z } from "zod";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { SITE } from "@/lib/site";

const schema = z.object({
    name: z.string().min(2, "Please enter your name"),
    phone: z
        .string()
        .min(7, "Please enter a valid phone number")
        .regex(/^[0-9+\-\s().]+$/, "Numbers, spaces, dashes only"),
    email: z.string().email("Please enter a valid email"),
    serviceType: z.string().min(1, "Choose a service"),
    address: z.string().min(3, "Property address is required"),
    description: z.string().min(10, "Tell us a bit more about the work"),
    contactMethod: z.enum(["phone", "email", "text"]),
    referral: z.string().optional(),
});

const initialState = {
    name: "",
    phone: "",
    email: "",
    serviceType: "",
    address: "",
    description: "",
    contactMethod: "phone",
    referral: "",
};

export default function Contact() {
    useSEO({
        title: `Get a Free Estimate — ${SITE.brand}`,
        description: `Request a free, no-obligation painting estimate in ${SITE.serviceArea}. We typically respond within 24 hours.`,
    });

    const [form, setForm] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
        if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const parsed = schema.safeParse(form);
        if (!parsed.success) {
            const fieldErrors = {};
            parsed.error.issues.forEach((iss) => {
                fieldErrors[iss.path[0]] = iss.message;
            });
            setErrors(fieldErrors);
            return;
        }
        setSubmitting(true);
        // Phase 1 is a static site. The backend (api.cprpainting.com) will
        // be wired up in Project B. For now, simulate success so the form
        // behaves as it will once the API is live.
        await new Promise((r) => setTimeout(r, 700));
        setSubmitting(false);
        setSubmitted(true);
        setForm(initialState);
    };

    return (
        <main data-testid="page-contact">
            <section
                style={{ background: "var(--brand-cream)" }}
                className="section-pad"
            >
                <div className="brand-container fade-rise">
                    <span className="eyebrow">Get a free estimate</span>
                    <h1
                        className="h1"
                        style={{
                            marginTop: "1rem",
                            marginBottom: "1rem",
                            maxWidth: 820,
                        }}
                        data-testid="contact-headline"
                    >
                        Tell us about the space.{" "}
                        <span style={{ color: "var(--brand-red)" }}>
                            We'll handle the rest.
                        </span>
                    </h1>
                    <p
                        style={{
                            color: "var(--brand-muted)",
                            fontSize: "1.125rem",
                            maxWidth: 640,
                        }}
                    >
                        Send us a few details and we'll reach out to schedule a
                        walk-through. Estimates are free, written, and detailed.
                    </p>
                </div>
            </section>

            <section
                className="section-pad"
                style={{ background: "#fff" }}
            >
                <div
                    className="brand-container"
                    style={{
                        display: "grid",
                        gap: "3rem",
                        gridTemplateColumns:
                            "minmax(0, 1.4fr) minmax(0, 1fr)",
                    }}
                >
                    {/* FORM */}
                    <div>
                        {submitted ? (
                            <div
                                className="brand-card"
                                data-testid="contact-success"
                                style={{
                                    background: "var(--brand-cream)",
                                    borderColor: "rgba(46,125,50,0.4)",
                                    padding: "2rem",
                                    textAlign: "center",
                                }}
                            >
                                <div
                                    style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: "50%",
                                        background: "var(--brand-success)",
                                        color: "#fff",
                                        display: "grid",
                                        placeItems: "center",
                                        margin: "0 auto 1rem",
                                    }}
                                >
                                    <CheckCircle2 size={28} />
                                </div>
                                <h3 className="h3">Thanks! We got it.</h3>
                                <p
                                    style={{
                                        color: "var(--brand-muted)",
                                        marginTop: "0.6rem",
                                        maxWidth: 460,
                                        marginLeft: "auto",
                                        marginRight: "auto",
                                    }}
                                >
                                    A member of the CPR Paint team will be in
                                    touch within 24 hours to schedule your
                                    walk-through.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setSubmitted(false)}
                                    data-testid="contact-success-new"
                                    style={{ marginTop: "1.5rem" }}
                                >
                                    Submit another request
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                noValidate
                                data-testid="contact-form"
                                style={{
                                    display: "grid",
                                    gap: "1.25rem",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(220px, 1fr))",
                                }}
                            >
                                <div>
                                    <label
                                        className="field-label"
                                        htmlFor="name"
                                    >
                                        Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        className="field-input"
                                        data-testid="contact-input-name"
                                    />
                                    {errors.name && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-name"
                                        >
                                            {errors.name}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label
                                        className="field-label"
                                        htmlFor="phone"
                                    >
                                        Phone
                                    </label>
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        autoComplete="tel"
                                        value={form.phone}
                                        onChange={handleChange}
                                        className="field-input"
                                        data-testid="contact-input-phone"
                                    />
                                    {errors.phone && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-phone"
                                        >
                                            {errors.phone}
                                        </div>
                                    )}
                                </div>

                                <div style={{ gridColumn: "1 / -1" }}>
                                    <label
                                        className="field-label"
                                        htmlFor="email"
                                    >
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        className="field-input"
                                        data-testid="contact-input-email"
                                    />
                                    {errors.email && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-email"
                                        >
                                            {errors.email}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label
                                        className="field-label"
                                        htmlFor="serviceType"
                                    >
                                        Service Type
                                    </label>
                                    <select
                                        id="serviceType"
                                        name="serviceType"
                                        value={form.serviceType}
                                        onChange={handleChange}
                                        className="field-select"
                                        data-testid="contact-input-service"
                                    >
                                        <option value="">
                                            Choose a service…
                                        </option>
                                        <option value="commercial">
                                            Commercial Painting
                                        </option>
                                        <option value="specialty">
                                            Specialty Finishes
                                        </option>
                                        <option value="exterior">
                                            Exterior Painting
                                        </option>
                                        <option value="interior">
                                            Interior Painting
                                        </option>
                                        <option value="other">Other</option>
                                    </select>
                                    {errors.serviceType && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-service"
                                        >
                                            {errors.serviceType}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label
                                        className="field-label"
                                        htmlFor="referral"
                                    >
                                        How did you hear about us?{" "}
                                        <span
                                            style={{
                                                color: "var(--brand-muted)",
                                                fontWeight: 400,
                                            }}
                                        >
                                            (optional)
                                        </span>
                                    </label>
                                    <select
                                        id="referral"
                                        name="referral"
                                        value={form.referral}
                                        onChange={handleChange}
                                        className="field-select"
                                        data-testid="contact-input-referral"
                                    >
                                        <option value="">Select one…</option>
                                        <option value="google">
                                            Google search
                                        </option>
                                        <option value="referral">
                                            Friend or neighbor
                                        </option>
                                        <option value="social">
                                            Social media
                                        </option>
                                        <option value="sign">
                                            Saw our truck / sign
                                        </option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div style={{ gridColumn: "1 / -1" }}>
                                    <label
                                        className="field-label"
                                        htmlFor="address"
                                    >
                                        Property Address
                                    </label>
                                    <input
                                        id="address"
                                        name="address"
                                        type="text"
                                        autoComplete="street-address"
                                        value={form.address}
                                        onChange={handleChange}
                                        className="field-input"
                                        data-testid="contact-input-address"
                                    />
                                    {errors.address && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-address"
                                        >
                                            {errors.address}
                                        </div>
                                    )}
                                </div>

                                <div style={{ gridColumn: "1 / -1" }}>
                                    <label
                                        className="field-label"
                                        htmlFor="description"
                                    >
                                        Tell us about the work
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        rows={5}
                                        value={form.description}
                                        onChange={handleChange}
                                        className="field-textarea"
                                        data-testid="contact-input-description"
                                        placeholder="e.g. Repaint two upstairs bedrooms and refinish kitchen cabinets — looking to start in the next 4-6 weeks."
                                    />
                                    {errors.description && (
                                        <div
                                            className="field-error"
                                            data-testid="contact-error-description"
                                        >
                                            {errors.description}
                                        </div>
                                    )}
                                </div>

                                <fieldset
                                    style={{
                                        gridColumn: "1 / -1",
                                        border: "none",
                                        padding: 0,
                                        margin: 0,
                                    }}
                                >
                                    <legend
                                        className="field-label"
                                        style={{ marginBottom: "0.6rem" }}
                                    >
                                        Preferred contact method
                                    </legend>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "0.75rem",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {["phone", "email", "text"].map(
                                            (method) => (
                                                <label
                                                    key={method}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: "0.45rem",
                                                        padding:
                                                            "0.55rem 0.9rem",
                                                        borderRadius: 8,
                                                        border:
                                                            form.contactMethod ===
                                                            method
                                                                ? "1.5px solid var(--brand-red)"
                                                                : "1.5px solid var(--brand-surface)",
                                                        background:
                                                            form.contactMethod ===
                                                            method
                                                                ? "rgba(210,5,28,0.06)"
                                                                : "#fff",
                                                        cursor: "pointer",
                                                        textTransform:
                                                            "capitalize",
                                                        fontWeight: 600,
                                                        fontSize: "0.875rem",
                                                    }}
                                                    data-testid={`contact-radio-${method}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="contactMethod"
                                                        value={method}
                                                        checked={
                                                            form.contactMethod ===
                                                            method
                                                        }
                                                        onChange={handleChange}
                                                        style={{
                                                            accentColor:
                                                                "var(--brand-red)",
                                                        }}
                                                    />
                                                    {method}
                                                </label>
                                            ),
                                        )}
                                    </div>
                                </fieldset>

                                <div
                                    style={{
                                        gridColumn: "1 / -1",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "0.75rem",
                                    }}
                                >
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting}
                                        data-testid="contact-submit"
                                        style={{
                                            width: "100%",
                                            padding: "1rem 1.25rem",
                                            fontSize: "1rem",
                                            opacity: submitting ? 0.7 : 1,
                                            cursor: submitting
                                                ? "wait"
                                                : "pointer",
                                        }}
                                    >
                                        {submitting
                                            ? "Sending…"
                                            : "Request my free estimate"}
                                    </button>
                                    <p
                                        style={{
                                            fontSize: "0.8125rem",
                                            color: "var(--brand-muted)",
                                            textAlign: "center",
                                        }}
                                    >
                                        We typically respond within 24 hours.
                                    </p>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* SIDE INFO */}
                    <aside
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                        }}
                        data-testid="contact-sidebar"
                    >
                        <div
                            className="brand-card"
                            style={{
                                background: "var(--brand-dark)",
                                color: "#fff",
                                borderColor: "transparent",
                            }}
                        >
                            <span
                                className="eyebrow"
                                style={{ color: "#fff" }}
                            >
                                Talk to a human
                            </span>
                            <h3
                                className="h3"
                                style={{
                                    color: "#fff",
                                    marginTop: "0.85rem",
                                    marginBottom: "0.85rem",
                                }}
                            >
                                Prefer to call?
                            </h3>
                            <ul
                                style={{
                                    listStyle: "none",
                                    padding: 0,
                                    margin: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.7rem",
                                    color: "#dcd9d2",
                                    fontSize: "0.95rem",
                                }}
                            >
                                <li
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <Phone
                                        size={16}
                                        color="var(--brand-red)"
                                    />
                                    <a
                                        href={`tel:${SITE.phoneLink}`}
                                        data-testid="contact-info-phone"
                                    >
                                        {SITE.phoneDisplay}
                                    </a>
                                </li>
                                <li
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <Mail
                                        size={16}
                                        color="var(--brand-red)"
                                    />
                                    <a
                                        href={`mailto:${SITE.email}`}
                                        data-testid="contact-info-email"
                                    >
                                        {SITE.email}
                                    </a>
                                </li>
                                <li
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <Clock
                                        size={16}
                                        color="var(--brand-red)"
                                    />
                                    <span data-testid="contact-info-hours">
                                        {SITE.hours}
                                    </span>
                                </li>
                                <li
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <MapPin
                                        size={16}
                                        color="var(--brand-red)"
                                    />
                                    <span data-testid="contact-info-area">
                                        {SITE.serviceAreaExtended}
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <div className="brand-card">
                            <h4
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: "1rem",
                                    marginBottom: "0.5rem",
                                }}
                            >
                                What happens next
                            </h4>
                            <ol
                                style={{
                                    paddingLeft: "1.1rem",
                                    margin: 0,
                                    color: "var(--brand-muted)",
                                    fontSize: "0.9rem",
                                    lineHeight: 1.6,
                                }}
                            >
                                <li>We respond within 24 hours.</li>
                                <li>
                                    Schedule a free on-site walk-through (or
                                    virtual for simple projects).
                                </li>
                                <li>
                                    You receive a detailed written estimate —
                                    line-itemed, with a clear scope.
                                </li>
                                <li>You decide. No pressure, ever.</li>
                            </ol>
                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
}
